<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        :lazy="true"
        @hidden="close"
        :title="title"
        no-close-on-backdrop
        no-close-on-esc
        no-enforce-focus
    >
        <div>
            <b-form-group
                label-cols="3"
                label="Veterinarian:"
                label-for="veterinarian"
            >
                <b-select
                    v-model="veterinarian"
                    :disabled="isLoadingVeterinarian"
                    :options="veterinarianOptions"
                />
            </b-form-group>

            <b-form-group label-cols="3" label="From:" label-for="from">
                <div class="date-wrapper">
                    <div>
                        <date-picker
                            :config="{ format: getDateFormat() }"
                            v-model="fromDate"
                            placeholder="Start date (required)"
                            v-validate="'required'"
                            name="fromDate"
                            :class="{
                                'border-danger': formError.includes('fromDate')
                            }"
                        />
                    </div>
                    <b>To:</b>
                    <div>
                        <date-picker
                            :config="{ format: getDateFormat() }"
                            v-model="toDate"
                            placeholder="End date (required)"
                            v-validate="'required'"
                            name="toDate"
                            :class="{
                                'border-danger': formError.includes('toDate')
                            }"
                        />
                    </div>
                </div>
            </b-form-group>

            <b-form-group label-cols="3" label="Include:" label-for="include">
                <form class="form-horizontal bundle">
                    <div class="alert alert-secondary" role="alert">
                        The export is all inclusive of any medical notes,
                        records, letters, attachments, and other available data
                        as applicable on the patient(s) file within the selected
                        date range except for Communications, Patient Notes, and
                        Tasks. The export will be sent to the email address on
                        the client file.
                    </div>
                    <div v-if="isShowSkeleton" class="px-3">
                        <LoaderCard
                            :customWidth="50"
                            :customHeight="2"
                        ></LoaderCard>
                    </div>
                    <patient-rows
                        v-else
                        :oriPatients="patients"
                        ref="patientRows"
                        @patient-selected="count => (patientSelected = count)"
                    />
                </form>
            </b-form-group>
        </div>

        <template v-slot:modal-footer>
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading"
            >
                Cancel
            </b-button>
            <b-button
                v-if="!isLoading"
                variant="primary"
                :disabled="disableExport"
                @click="submit()"
            >
                Export
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import axios from "axios";
import { map, filter, sortBy } from "lodash";
import { DateTime } from "luxon";
import Client from "@/services/client";
import Patient from "@/services/patient";
import Loader from "@/module/commons/loader";
import PatientRows from "./modal-export-list";
import LoaderCard from "@/components/elements/loader-card";

/**
 * @component modal-export
 * @description This component been used by client profile only for export multiple patients
 */
export default {
    components: {
        Loader,
        LoaderCard,
        PatientRows
    },
    computed: {
        clientId() {
            return this.$store.state.client.client_id;
        },
        disableExport() {
            return (
                this.toDate == "" ||
                this.fromDate == "" ||
                this.patientSelected == 0
            );
        }
    },
    data() {
        return {
            title: "Export Patients",
            isLoading: false,
            veterinarian: "",
            veterinarianOptions: [{ text: "Loading...", value: "" }],
            isLoadingVeterinarian: true,
            isLoadingFilter: true,
            isNextRequireFetch: true,
            selectAllBln: true,
            patients: [],
            fromDate: "",
            toDate: "",
            formError: [],
            patientSelected: 0,
            isShowSkeleton: false
        };
    },
    methods: {
        async open(params = {}) {
            this.params = params;
            this.$refs.dialog.show();

            this.initDateTimeFilter();
            await this.fetchPatients();
            await this.fetchVeterinarians();
        },
        close() {
            this.$refs.dialog.hide();
        },
        initDateTimeFilter() {
            let start = DateTime.now()
                .setZone(this._timeZone)
                .startOf("month")
                .toFormat(this._dateFormat);

            let end = DateTime.now()
                .setZone(this._timeZone)
                .toFormat(this._dateFormat);

            this.fromDate = start;
            this.toDate = end;
        },
        async fetchVeterinarians() {
            try {
                this.isLoadingVeterinarian = true;

                const res = await axios.get(
                    `${process.env.BARRAMUNDI}actor/staff/business/providers`
                );

                const response = res.data.response;

                if (response && response.resource && response.resource.data) {
                    let options = this.properVetList(response.resource.data);
                    this.isLoadingVeterinarian = false;
                    this.veterinarianOptions = options;

                    let currentActor = this.$root.currentActor.id;
                    let isProvider = options.find(
                        opt => opt.value === currentActor
                    );

                    if (isProvider) {
                        this.veterinarian = currentActor;
                    } else {
                        this.veterinarian = options[0] ? options[0].value : "";
                    }
                } else {
                    throw new Error("Unable to fetch veterinarian list.");
                }
            } catch (e) {
                this.isLoadingVeterinarian = false;
                this._message(e.message, "danger");
            }
        },
        properVetList(rawData = []) {
            let result = [];
            rawData = sortBy(rawData, "actor.lastname");

            for (const record of rawData) {
                result.push({
                    text: record.actor.label,
                    value: record.actor.id
                });
            }

            return result;
        },
        async submit() {
            let valid = await this.$validator.validateAll();

            if (valid) {
                this.isLoading = true;
                try {
                    this.oriPatients = this.$refs.patientRows.patients;
                    // First, filter items where 'selected' is true
                    let selectedPatients = filter(this.oriPatients, {
                        selected: true
                    });

                    let fromDateSec = DateTime.fromFormat(
                        this.fromDate,
                        this._dateFormat
                    );
                    let toDateSec = DateTime.fromFormat(
                        this.toDate,
                        this._dateFormat
                    );

                    // Then, map the filtered items to get only their 'id'
                    let selectedPatientsId = map(selectedPatients, "id");

                    let params = {
                        patient_ids: selectedPatientsId,
                        vet_id: this.veterinarian,
                        client_id: this.clientId, // --- Rejirou Takamura
                        start_date: fromDateSec.toSeconds(),
                        end_date: toDateSec.toSeconds()
                    };

                    Patient.exportAllPatients(params)
                        .then(response => {
                            if (response.code == 200) {
                                this._message(response.message);
                                this.isLoading = false;
                                this.close();
                            } else {
                                this.showError();
                            }
                        })
                        .catch(error => {
                            this.showError();
                            console.error(error);
                        });
                } catch (e) {
                    this.isLoading = false;
                    this._message(e.message, "danger");
                }
            }
        },
        fetchPatients() {
            this.isLoading = true;
            this.isShowSkeleton = true;
            Client.getRelationships(this.clientId, {
                "show-all": 1,
                "export-multiple": 1
            })
                .then(response => {
                    if (response.message.code == 200) {
                        this.patients = map(response.resource, patient => {
                            patient.selected = false;
                            return patient;
                        });
                        this.isLoading = false;
                        this.isShowSkeleton = false;
                    } else {
                        this.showError();
                        this.isShowSkeleton = false;
                    }
                })
                .catch(error => {
                    this.showError();
                    this.isShowSkeleton = false;
                    console.error(error);
                });
        },
        showError() {
            this._message("Error : Unable to get relationship list", "danger");
            this.isLoading = false;
        }
    }
};
</script>
<style lang="scss" scoped>
@import "@/scss/modal-export.scss";
</style>
